/**
 * Componente ErrorBoundary
 *
 * Captura erros nao tratados em componentes React filhos
 * e exibe uma tela de fallback amigavel ao usuario,
 * evitando que a aplicacao inteira quebre.
 *
 * @author Equipe VendeAi
 * @since 1.0.0
 */

import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

/** Props do ErrorBoundary */
interface ErrorBoundaryProps {
  /** Componentes filhos */
  children: ReactNode;
  /** Componente de fallback customizado (opcional) */
  fallback?: ReactNode;
}

/** Estado interno do ErrorBoundary */
interface ErrorBoundaryState {
  /** Se houve erro */
  hasError: boolean;
  /** Mensagem do erro */
  error: Error | null;
}

/**
 * Componente de fronteira de erro
 *
 * Captura erros em qualquer componente filho e exibe
 * uma mensagem amigavel com opcao de recarregar.
 *
 * @example
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log do erro para depuracao
    console.error('[ErrorBoundary] Erro capturado:', error, errorInfo);
  }

  /** Recarrega a pagina */
  handleReload = (): void => {
    window.location.reload();
  };

  /** Tenta resetar o estado de erro */
  handleReset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Fallback customizado
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Fallback padrao
      return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4 dark:bg-gray-900">
          <div className="w-full max-w-md rounded-lg bg-white p-8 shadow-lg dark:bg-gray-800">
            <div className="flex flex-col items-center text-center">
              <div className="mb-4 rounded-full bg-red-100 p-4 dark:bg-red-900/30">
                <AlertTriangle size={32} className="text-red-600 dark:text-red-400" />
              </div>

              <h1 className="mb-2 text-xl font-bold text-gray-900 dark:text-white">
                Algo deu errado
              </h1>

              <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
                Ocorreu um erro inesperado na aplicacao. Tente recarregar a pagina
                ou voltar para a pagina inicial.
              </p>

              {this.state.error && (
                <div className="mb-6 w-full rounded-lg bg-gray-100 p-3 dark:bg-gray-700">
                  <p className="break-all text-left text-xs text-gray-600 dark:text-gray-300">
                    {this.state.error.message}
                  </p>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={this.handleReset}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  Tentar Novamente
                </button>
                <button
                  onClick={this.handleReload}
                  className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
                >
                  <RefreshCw size={16} />
                  Recarregar Pagina
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
